import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useOutletContext } from 'react-router';
import { useFormFactor } from '@/lib/formFactor';
import { useSession } from '@/stores/session';
import { Icon } from '@/components/Icon';
import { ViewSidebar } from '@/components/ViewSidebar';
import { Button, IconButton } from '@/components/Button';
import { Tag, Badge } from '@/components/Tag';
import { Alert, Empty, ErrorState, Skeleton } from '@/components/States';
import { legacyHref } from '@/shell/nav';
import { money, qty } from '@/lib/format';
import { Applied, Chips, DataTable, Denied, FilterButton, k, PhoneBar, SearchBox, type Col } from './ListKit';
import { FilterSheet } from './FilterSheet';
import { purchaseChips } from './PurchaseCards';
import { canCreateFromSuggestion, PurchaseCreateFromSuggestions } from './PurchaseCreateFromSuggestions';
import { PurchaseCreateDialog } from './PurchaseCreateDialog';
import { usePurchaseCounts, useResource, useSuppliers, useUrlState, useWarehouses } from './api';
import type { PurchaseSuggestion, SuggestionPayload } from './types';
import s from './PurchaseSuggestionsPage.module.css';

const NO_SUPPLIER = 'none';
const WRITES_ENABLED = import.meta.env.VITE_READONLY === '0';

function rowKey(x: PurchaseSuggestion) {
  return [x.warehouseId, x.productId, x.supplierId || '', x.packagingOptionId || ''].join(':');
}

function csvCell(value: string | number) {
  const raw = String(value);
  const safe = /^[\s]*[=+@-]/.test(raw) ? `'${raw}` : raw;
  return `"${safe.replaceAll('"', '""')}"`;
}

/** 「12 箱 × 50 个」；没有包装换算时退回包装方案名（老系统「包装建议」列） */
function packAdvice(x: PurchaseSuggestion) {
  if (x.suggestedPackageQuantity && x.unitsPerPackage) return `${qty(x.suggestedPackageQuantity)} ${x.packageUnit || '件'} × ${qty(x.unitsPerPackage)} 个`;
  return x.packagingName || '';
}

/** 关联订单号：进 v2 订单列表按单号搜索（接口只给单号，不给订单 id） */
function OrderLinks({ nos, max }: { nos: string[]; max: number }) {
  return (
    <span className={s.orders}>
      {nos.slice(0, max).map((no, i) => (
        <span key={no}>
          {i > 0 && '、'}
          <Link to={`/orders?q=${encodeURIComponent(no)}`} onClick={(e) => e.stopPropagation()} className="tnum">{no}</Link>
        </span>
      ))}
      {nos.length > max && <span className="em"> 等 {nos.length} 张</span>}
    </span>
  );
}

/** 采购建议卡（P1）：本地开发允许勾选完整报价的建议。 */
function SuggestionCard({ x, whName, checked, onSelect }: { x: PurchaseSuggestion; whName?: string; checked?: boolean; onSelect?: () => void }) {
  const pack = packAdvice(x);
  return (
    <article className={[s.card, checked && s.selectedCard, !x.supplierName && s.warnCard].filter(Boolean).join(' ')}>
      <div className={s.h}>
        {onSelect && <input className={s.check} type="checkbox" aria-label={`选择采购建议 ${x.productName}`} checked={!!checked} disabled={!canCreateFromSuggestion(x)} title={canCreateFromSuggestion(x) ? undefined : '缺少供应商、包装方案或单价'} onChange={onSelect} />}
        <b>{x.productName}</b>
        {!x.supplierName && <Tag tone="amber" sm>缺供应商</Tag>}
      </div>
      <div className={s.sub}>
        <span className="tnum">{x.productCode}</span>
        {whName && <span>{whName}</span>}
        <span>{x.supplierName || '未设默认供应商'}</span>
      </div>
      <div className={s.m}>
        <div><span>缺口</span><b className="tnum neg">{qty(x.shortageQuantity)}</b></div>
        <div><span>建议采购</span><b className="tnum">{qty(x.suggestedQuantity)}</b></div>
        <div><span>预计金额</span><b className="tnum">{x.estimatedAmount != null ? money(x.estimatedAmount) : '—'}</b></div>
      </div>
      <div className={s.f}>
        <span className={s.pack}>{pack ? <><i>包装</i>{pack}</> : <span className="em">没有包装报价</span>}</span>
        <span className={s.rel}>关联 <b className="tnum">{x.orderCount}</b> 张订单</span>
      </div>
      {x.orderNos.length > 0 && <div className={s.nos}><OrderLinks nos={x.orderNos} max={3} /></div>}
    </article>
  );
}

/**
 * 采购建议（老系统 /purchase-suggestions）：按未完成订单的库存缺口，按产品 × 供应商 × 包装方案汇总。
 * 接口一次返回缺口最大的 50 条、无分页，搜索和筛选在前端做。
 * 手机卡片；iPad / 桌面表格。「勾选生成采购单」是写操作，只读期去老版。
 */
export function PurchaseSuggestionsPage() {
  const ff = useFormFactor();
  const nav = useNavigate();
  const can = useSession((st) => st.can);
  const allowed = can('purchase.suggestions.read');
  const canPO = can('purchase.read');
  const [sp, set] = useUrlState();
  const q = (sp.get('q') || '').trim();
  const warehouseId = sp.get('warehouseId') || undefined;
  const supplier = sp.get('supplier') || undefined;
  const phone = ff === 'phone';
  const [panel, setPanel] = useState(false);
  const [searching, setSearching] = useState(!!q);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [grouped, setGrouped] = useState(false);
  const [productSortDesc, setProductSortDesc] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createDraft, setCreateDraft] = useState<PurchaseSuggestion[] | null>(null);
  const setPhoneTabBarHidden = useOutletContext<{ setPhoneTabBarHidden: (hidden: boolean) => void } | undefined>()?.setPhoneTabBarHidden;

  const { data, loading, error, reload } = useResource<SuggestionPayload>(allowed ? '/api/orders/purchase-suggestions' : null);
  const { counts } = usePurchaseCounts({}, allowed && canPO);
  const suppliers = useSuppliers(allowed && can('purchase.create') && WRITES_ENABLED);
  const warehouses = useWarehouses(allowed);

  const all = useMemo(() => data?.purchaseSuggestions || [], [data]);
  const rows = useMemo(() => {
    const kw = q.toLowerCase();
    return all.filter((x) => {
      if (warehouseId && x.warehouseId !== warehouseId) return false;
      if (supplier === NO_SUPPLIER ? !!x.supplierName : supplier && x.supplierId !== supplier) return false;
      if (!kw) return true;
      return [x.productCode, x.productName, x.supplierName || '', ...x.orderNos].some((v) => v.toLowerCase().includes(kw));
    });
  }, [all, q, warehouseId, supplier]);
  const phoneRows = useMemo(() => [...rows].sort((a, b) => {
    const order = a.productName.localeCompare(b.productName, 'zh-CN') || a.productCode.localeCompare(b.productCode);
    return productSortDesc ? -order : order;
  }), [rows, productSortDesc]);
  const canCreate = can('purchase.create');
  const writable = WRITES_ENABLED;
  const selectedCount = all.filter((item) => selectedKeys.includes(rowKey(item)) && canCreateFromSuggestion(item)).length;

  useEffect(() => {
    setPhoneTabBarHidden?.(phone && allowed && canCreate && WRITES_ENABLED && selectedCount > 0);
  }, [setPhoneTabBarHidden, phone, allowed, canCreate, selectedCount]);
  useEffect(() => () => setPhoneTabBarHidden?.(false), [setPhoneTabBarHidden]);

  if (!allowed) return <Denied what="查看采购建议" perm="查看采购建议" />;

  const whName = (id: string) => warehouses.find((w) => w.id === id)?.name;
  const exportSuggestions = () => {
    const header = ['产品编号', '产品名称', '仓库', '缺口', '建议采购', '包装建议', '建议供应商', '预计金额', '关联订单数', '订单号'];
    const values = rows.map((item) => [item.productCode, item.productName, whName(item.warehouseId) || '', item.shortageQuantity, item.suggestedQuantity, packAdvice(item), item.supplierName || '', item.estimatedAmount ?? '', item.orderCount, item.orderNos.join('、')]);
    const content = [header, ...values].map((line) => line.map(csvCell).join(',')).join('\r\n');
    const url = URL.createObjectURL(new Blob([`\uFEFF${content}`], { type: 'text/csv;charset=utf-8' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `采购建议-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const supplierOpts = Array.from(new Map(all.filter((x) => x.supplierId).map((x) => [x.supplierId!, x.supplierName || ''])).entries()).map(([value, label]) => ({ value, label }));
  if (all.some((x) => !x.supplierName)) supplierOpts.push({ value: NO_SUPPLIER, label: '未设默认供应商' });
  const estimated = rows.reduce((a, x) => a + (x.estimatedAmount ?? 0), 0);
  const hasEstimate = rows.some((x) => x.estimatedAmount != null);
  const supplierCount = new Set(rows.map((x) => x.supplierId || NO_SUPPLIER)).size;
  const missing = all.filter((x) => !x.supplierName).length;
  const filterCount = Number(!!warehouseId) + Number(!!supplier);
  const selectedSuggestions = all.filter((item) => selectedKeys.includes(rowKey(item)) && canCreateFromSuggestion(item));
  const selectedEstimate = selectedSuggestions.reduce((sum, item) => sum + (item.estimatedAmount ?? item.suggestedQuantity * Number(item.unitCost || 0)), 0);
  const selectedSuppliers = new Set(selectedSuggestions.map((item) => item.supplierId)).size;
  const selectedGroups = new Set(selectedSuggestions.map((item) => `${item.supplierId}:${item.warehouseId}`)).size;
  const selectableRows = rows.filter(canCreateFromSuggestion);
  const allVisibleSelected = selectableRows.length > 0 && selectableRows.every((item) => selectedKeys.includes(rowKey(item)));
  const toggleSelectAll = () => {
    const keys = selectableRows.map(rowKey);
    setSelectedKeys((current) => allVisibleSelected ? current.filter((key) => !keys.includes(key)) : [...new Set([...current, ...keys])]);
  };
  const tableRows = grouped ? [...rows].sort((a, b) => (a.supplierName || '').localeCompare(b.supplierName || '', 'zh-CN') || a.warehouseId.localeCompare(b.warehouseId) || b.shortageQuantity - a.shortageQuantity) : rows;
  const groupLabel = (item: PurchaseSuggestion) => `${item.supplierName || '未设默认供应商'} · ${whName(item.warehouseId) || '仓库'}`;
  const toggleSelected = (item: PurchaseSuggestion) => setSelectedKeys((current) => current.includes(rowKey(item)) ? current.filter((key) => key !== rowKey(item)) : [...current, rowKey(item)]);
  const beginCreate = () => { if (selectedSuggestions.length) setCreateDraft(selectedSuggestions); };
  const removeDraftSuggestion = (item: PurchaseSuggestion) => {
    const key = rowKey(item);
    setSelectedKeys((current) => current.filter((selected) => selected !== key));
    setCreateDraft((current) => {
      if (!current) return null;
      const next = current.filter((suggestion) => rowKey(suggestion) !== key);
      return next.length ? next : null;
    });
  };
  const createDialog = createDraft && <PurchaseCreateFromSuggestions suggestions={createDraft} warehouses={warehouses} onClose={() => setCreateDraft(null)} onCreated={() => { setSelectedKeys([]); reload(); }} onRemove={removeDraftSuggestion} />;
  const blankCreateDialog = createOpen && writable && <PurchaseCreateDialog suppliers={suppliers} warehouses={warehouses} onClose={() => setCreateOpen(false)} onCreated={reload} />;

  const chips = purchaseChips({
    active: 'suggest',
    counts,
    suggestCount: data?.purchaseSuggestionCount,
    canSuggest: true,
    canPO,
    pick: (key) => key !== 'suggest' && nav(key === 'ready' ? '/purchase-orders' : `/purchase-orders?view=${key}`),
  });

  const applied = (
    <Applied
      items={[
        ...(warehouseId ? [{ key: 'wh', label: whName(warehouseId) || '指定仓库', onRemove: () => set({ warehouseId: undefined }) }] : []),
        ...(supplier ? [{ key: 'sup', label: supplierOpts.find((o) => o.value === supplier)?.label || '指定供应商', onRemove: () => set({ supplier: undefined }) }] : []),
      ]}
      onClear={() => set({ warehouseId: undefined, supplier: undefined })}
    />
  );

  const search = (cls?: string) => (
    <SearchBox className={cls} value={q} autoFocus={phone && searching && !q} placeholder="产品 / 供应商 / 订单号" onSearch={(v) => set({ q: v || undefined })} />
  );

  const panelEl = panel && (
    <FilterSheet
      sheet={phone}
      value={{ warehouseId, supplier }}
      onClose={() => setPanel(false)}
      onApply={(f) => {
        setPanel(false);
        set({ warehouseId: f.warehouseId, supplier: f.supplier });
      }}
      groups={[
        { key: 'supplier', title: '建议供应商', opts: supplierOpts },
        { key: 'warehouseId', title: '仓库', opts: warehouses.filter((w) => all.some((x) => x.warehouseId === w.id)).map((w) => ({ value: w.id, label: w.name })) },
      ]}
      note="建议按缺口从大到小排列。供应商取产品包装方案的默认报价。"
    />
  );

  const notes = data && (
    <>
      {missing > 0 && (
        <div className={s.alert}>
          <Alert tone="warn" title={`${missing} 条建议缺少默认供应商或包装报价`}>需要先在老版「产品资料 · 包装方案」里维护供应商报价，才能按建议生成采购单。</Alert>
        </div>
      )}
      {data.purchaseSuggestionCount > all.length && (
        <p className={s.cap}>共 {data.purchaseSuggestionCount} 条建议，这里显示缺口最大的 {all.length} 条。</p>
      )}
    </>
  );

  const body = (() => {
    if (error) return <ErrorState title="采购建议加载失败" message={error.message} detail="GET /api/orders/purchase-suggestions" onRetry={reload} />;
    if (loading || !data) return <div className={k.pad}><Skeleton lines={3} /><Skeleton lines={3} /></div>;
    if (!all.length) return <Empty icon="ok" title="没有需要补货的缺口">所有未完成订单的库存都够了。可用为负的产品会在这里出现采购建议。</Empty>;
    if (!rows.length) return <Empty icon="srch" title="没有符合条件的建议">换个关键词，或者清空筛选再看看。</Empty>;
    return null;
  })();

  const cols: Col<PurchaseSuggestion>[] = [
    ...(canCreate && WRITES_ENABLED ? [{ key: 'select', title: <input className={s.check} type="checkbox" aria-label="全选当前筛选结果中可生成的采购建议" checked={allVisibleSelected} disabled={!selectableRows.length} ref={(node) => { if (node) node.indeterminate = !allVisibleSelected && selectableRows.some((item) => selectedKeys.includes(rowKey(item))); }} onChange={toggleSelectAll} />, w: 52, render: (x: PurchaseSuggestion) => <input className={s.check} type="checkbox" aria-label={`选择采购建议 ${x.productName}`} checked={selectedKeys.includes(rowKey(x))} disabled={!canCreateFromSuggestion(x)} title={canCreateFromSuggestion(x) ? undefined : '缺少供应商、包装方案或单价'} onChange={() => toggleSelected(x)} /> } as Col<PurchaseSuggestion>] : []),
    { key: 'p', title: '产品', w: 200, fix: true, render: (x) => <div className={k.cell2}><span>{x.productName}</span><small className="tnum">{x.productCode}</small></div> },
    { key: 'wh', title: '仓库', w: 96, render: (x) => whName(x.warehouseId) || <span className="em">—</span> },
    { key: 'short', title: '缺口', w: 84, align: 'r', render: (x) => <b className="neg">{qty(x.shortageQuantity)}</b> },
    { key: 'sug', title: '建议采购', w: 96, align: 'r', render: (x) => qty(x.suggestedQuantity) },
    { key: 'pack', title: '包装建议', w: 150, render: (x) => packAdvice(x) || <span className="em">—</span> },
    { key: 'sup', title: '建议供应商', w: 140, render: (x) => x.supplierName || <span className={s.miss}>未设默认供应商</span> },
    { key: 'amt', title: '预计金额', w: 120, align: 'r', render: (x) => (x.estimatedAmount != null ? money(x.estimatedAmount) : <span className="em">—</span>) },
    { key: 'cnt', title: '关联订单', w: 84, align: 'r', render: (x) => <Badge tone={x.orderCount > 1 ? 'red' : 'grey'}>{x.orderCount}</Badge> },
    { key: 'nos', title: '订单号', w: 300, render: (x) => <OrderLinks nos={x.orderNos} max={ff === 'tablet' ? 2 : 3} /> },
  ];
  const tabletWidths: Record<string, number> = { select: 40, p: 130, short: 60, sug: 70, pack: 95, sup: 120, amt: 90, cnt: 52, nos: 150 };
  const tabletCols = cols.filter((col) => col.key !== 'wh').map((col) => ({ ...col, w: tabletWidths[col.key] || col.w }));

  const newPO = canCreate && (WRITES_ENABLED ?
    <Button icon="plus" variant="primary" disabled={!selectedSuggestions.length} disabledReason="先勾选有供应商、包装方案和报价的采购建议" onClick={beginCreate}>按供应商批量生成采购单</Button> :
    <a href={legacyHref('/purchase-orders')} className={`${k.linkBtn} ${k.primaryLink}`} title="生产只读期：按建议生成采购单在老版进行"><Icon name="plus" />去老版新建采购单</a>);

  const selectionBar = canCreate && WRITES_ENABLED && selectedSuggestions.length > 0 && (
    <div className={s.selectionBar} role="region" aria-label="已选采购建议">
      <div className={s.selectionCount}><b>已选 {selectedSuggestions.length} 条 · {money(selectedEstimate)}</b><span>{selectedSuppliers} 家供应商，会生成 {selectedGroups} 张采购单</span></div>
      <button type="button" className={s.selectionSecondary} onClick={toggleSelectAll}>{allVisibleSelected ? '取消全选' : '全选'}</button>
      {!phone && <button type="button" className={s.selectionSecondary} aria-pressed={grouped} onClick={() => setGrouped((current) => !current)}>{grouped ? '取消分组' : '按供应商分组'}</button>}
      <button type="button" className={s.selectionPrimary} onClick={beginCreate}>生成采购单</button>
    </div>
  );

  /* ------------------------------ 手机 ------------------------------ */
  if (phone) {
    return (
      <div className={k.phone}>
        <PhoneBar title="采购" back={{ label: '应用', to: '/apps', fallback: '/apps' }} actions={<>
          <IconButton name="srch" label="搜索" onClick={() => setSearching((x) => !x)} />
          {canCreate && (writable ? <button type="button" className={k.pIcon} onClick={() => setCreateOpen(true)} aria-label="新建采购单"><Icon name="plus" size={22} /></button> : <a className={k.pIcon} href={legacyHref('/purchase-orders')} aria-label="新建采购单（老版）"><Icon name="plus" size={22} /></a>)}
        </>} />
        {(searching || q) && <div className={k.psearch}>{search()}</div>}
        <div className={k.chipbar}>
          <Chips items={chips} inset="phone" />
          <FilterButton count={filterCount} onClick={() => setPanel(true)} />
        </div>
        {applied}
        <div className={k.pbody}>
          <div className={k.listbar}>
            <span className="tnum">{rows.length} 条 · {supplierCount} 家供应商{hasEstimate && ` · 预计 ${money(estimated)}`}</span>
            <button type="button" className={s.sortButton} onClick={() => setProductSortDesc((value) => !value)}>{productSortDesc ? '按产品降序' : '按产品'}<Icon name="sort" size={14} /></button>
          </div>
          {notes}
          {body || phoneRows.map((x) => <SuggestionCard key={rowKey(x)} x={x} whName={whName(x.warehouseId)} checked={selectedKeys.includes(rowKey(x))} onSelect={canCreate && WRITES_ENABLED ? () => toggleSelected(x) : undefined} />)}
          {canCreate && !WRITES_ENABLED && !body && (
            <div className={s.cta}>
              <a href={legacyHref('/purchase-orders')} className={s.ctaBtn}><Icon name="plus" />去老版新建采购单</a>
              <p>生产只读期：勾选建议生成采购单在老版进行。</p>
            </div>
          )}
        </div>
        {selectionBar}
        {panelEl}
        {createDialog}
        {blankCreateDialog}
      </div>
    );
  }

  if (ff === 'desktop') {
    return <div className={`${k.desk} ${k.desktopPage}`}>
      <header className={k.desktopHead}>
        <div><h1>采购建议</h1><p>根据未完成业务订单的库存缺口，按产品、供应商和包装方案汇总 <span>共 {rows.length} 条建议{hasEstimate ? ` · 预计 ${money(estimated)}` : ''}</span></p></div>
        <div className={k.desktopActions}><Button icon="down" disabled={!rows.length} onClick={exportSuggestions}>导出</Button>{newPO}</div>
      </header>
      <div className={k.desktopWork}>
        <ViewSidebar title="采购视图" active="suggest" onPick={(key) => nav(key === 'ready' ? '/purchase-orders' : `/purchase-orders?view=${key}`)} views={[
          { key: 'suggest', label: '待下单建议', icon: 'alert', count: data?.purchaseSuggestionCount, hot: true },
          ...(canPO ? [
            { key: 'ready', label: '已下单待到货', icon: 'inbox', count: (counts.ORDERED ?? 0) + (counts.PARTIALLY_RECEIVED ?? 0), to: '/purchase-orders' },
            { key: 'PARTIALLY_RECEIVED', label: '部分入库', icon: 'truck', count: counts.PARTIALLY_RECEIVED, to: '/purchase-orders?view=PARTIALLY_RECEIVED' },
            { key: 'DRAFT', label: '草稿', icon: 'flag', count: counts.DRAFT, to: '/purchase-orders?view=DRAFT', separator: true },
            { key: 'RECEIVED', label: '已入库', icon: 'box', count: counts.RECEIVED, to: '/purchase-orders?view=RECEIVED' },
            { key: 'CANCELLED', label: '已取消', icon: 'x', count: counts.CANCELLED, to: '/purchase-orders?view=CANCELLED' },
            { key: 'all', label: '全部采购单', icon: 'cards', count: counts.all, to: '/purchase-orders?view=all', separator: true },
          ] : []),
          { key: 'suppliers', label: '供应商', icon: 'team', to: '/suppliers' },
        ]} />
        <section className={k.desktopArea}>
          {applied}{notes}{selectionBar}
          {body ? <div className={k.dbody}>{body}</div> : <DataTable cols={cols.filter((c) => c.key !== 'wh')} rows={tableRows} rowKey={rowKey} isRowSelected={(item) => selectedKeys.includes(rowKey(item))} rowTone={(x) => (x.supplierName ? undefined : 'warn')} groupBy={grouped ? groupLabel : undefined} />}
          <footer className={k.pager}><span>{rows.length} 条建议 · {supplierCount} 家供应商{hasEstimate ? ` · 预计 ${money(estimated)}` : ''}</span></footer>
        </section>
      </div>
      {panelEl}
      {createDialog}
    </div>;
  }

  /* --------------------------- iPad：表格 --------------------------- */
  return (
    <div className={k.desk}>
      <header className={`${k.dh} ${k.inventoryHeader}`}>
        <h1>待下单建议<small className="tnum">{data ? `${rows.length} 条` : ''}</small></h1>
        <span className={k.sp} />
        <div className={k.toolbarActions} aria-label="采购建议操作，左右滑动查看全部">
          {search()}
          {ff !== 'tablet' && <FilterButton boxed count={filterCount} onClick={() => setPanel(true)} />}
          {ff !== 'tablet' && <Button icon="ref" onClick={reload}>刷新</Button>}
          <Button icon="down" disabled={!rows.length} onClick={exportSuggestions}>导出</Button>
          {newPO}
        </div>
      </header>
      <div className={k.dchips}><Chips items={chips} inset="desk" /></div>
      {applied}
      {notes}{selectionBar}
      {body ? <div className={k.dbody}>{body}</div> : ff === 'tablet'
        ? <div className={s.tabletTable}><DataTable cols={tabletCols} rows={tableRows} rowKey={rowKey} isRowSelected={(item) => selectedKeys.includes(rowKey(item))} rowTone={(x) => (x.supplierName ? undefined : 'warn')} groupBy={grouped ? groupLabel : undefined} /></div>
        : <DataTable cols={cols} rows={tableRows} rowKey={rowKey} isRowSelected={(item) => selectedKeys.includes(rowKey(item))} rowTone={(x) => (x.supplierName ? undefined : 'warn')} groupBy={grouped ? groupLabel : undefined} />}
      <footer className={k.pager}>
        <span>根据未完成订单的库存缺口，按产品、供应商和包装方案汇总；缺口从大到小。{ff === 'tablet' && ' 横向滑动可查看完整订单号。'}</span>
      </footer>
      {panelEl}
      {createDialog}
    </div>
  );
}
